// Korg DSS-1 / DSM-1 Disk Image Exporter
// Creates a genuine floppy disk image (.img) with 12-bit packed samples
// in the WTB multisound format that the DSS-1 can read.
//
// Disk geometry (DSS-1 standard density):
//   Total size: 819200 bytes (800 KB)
//   Sector size: 1024 bytes
//   80 tracks × 2 sides × 5 sectors/track = 800 sectors
//
// Disk layout:
//   0x0000 - 0x03FF : Boot sector (unused, filled with 0xFF)
//   0x0400 - 0x23FF : BAM + Directory (128 directory entries, 64 bytes each)
//   0x2400 - end    : Data area (sample data in 1024-byte sectors)
//
// Sample format: 12-bit packed (3 bytes → 2 samples)
//   Each 1024-byte sector holds 682 samples (341 groups × 2)
//   Last byte of each sector is padding (1024 % 3 = 1)

export const DSS1_DISK_SIZE = 0xC8000;       // 819200 bytes
export const DSS1_BAM = 0x400;
export const DSS1_DIR = 0x2000;
export const DSS1_DATA_OFFS = DSS1_BAM + DSS1_DIR;  // 0x2400
export const DSS1_SECTOR_SIZE = 1024;
export const DSS1_WTB_HEADER_SIZE = 0x15D;   // 349 bytes
export const DSS1_MAX_SECTORS = 800;         // 80 tracks × 2 sides × 5 sectors
export const DSS1_DATA_SECTORS = DSS1_MAX_SECTORS - Math.floor(DSS1_DATA_OFFS / DSS1_SECTOR_SIZE); // sectors available for data
export const DSS1_SAMPLES_PER_SECTOR = 682;  // 341 groups × 2 samples
export const DSS1_MAX_DIR_ENTRIES = 128;

// DSM-1 high-density disk (1.6 MB)
export const DSM1_DISK_SIZE = 0x190000;      // 1638400 bytes
export const DSM1_DATA_START = 0x10000;
export const DSM1_LST_OFFSET = 0x2400;
export const DSM1_PARAM_TABLE = 0x3000;

// Reverse of s2o: convert a byte offset back to sector number
// The DSS-1 uses interleaved sectors. The interleave map for writing is:
//   Physical sector order: 0,1,2,3,4  →  Logical: 0,1,2,3,4 (no interleave for writing)
// We write without interleave (interleave=0) which is the simplest case.
// s2o(s, 0) = (Math.floor(s/5)*5 + s%5) * 1024 = s * 1024 (linear)
// So without interleave, sector N is at offset N * 1024 from the data area start.

/**
 * Pack a single group (2 samples → 3 bytes) into output buffer at byteOff.
 */
function packGroup(out, byteOff, s0, s1) {
   const u0 = ((s0 >> 4) + 2048) & 0xFFF;
   const u1 = ((s1 >> 4) + 2048) & 0xFFF;
   out[byteOff]     = (u0 >> 4) & 0xFF;
   out[byteOff + 1] = ((u1 & 0xF) << 4) | (u0 & 0xF);
   out[byteOff + 2] = (u1 >> 4) & 0xFF;
}

/**
 * Encode 16-bit PCM samples into 12-bit packed format (3 bytes → 2 samples).
 * Each 1024-byte sector gets its own group alignment with 1 padding byte at the end.
 *
 * @param {Int16Array} samples - 16-bit PCM samples (will be down-converted to 12-bit)
 * @returns {Uint8Array} - packed bytes, sector-aligned with padding
 */
export function encodeDss1Samples(samples) {
   const totalGroups = Math.ceil(samples.length / 2);
   const groupsPerSector = Math.floor(DSS1_SECTOR_SIZE / 3); // 341
   const numSectors = Math.ceil(totalGroups / groupsPerSector);
   const totalBytes = numSectors * DSS1_SECTOR_SIZE;
   const out = new Uint8Array(totalBytes);

   let smpIdx = 0;
   for (let sec = 0; sec < numSectors; sec++) {
      const secBase = sec * DSS1_SECTOR_SIZE;
      for (let g = 0; g < groupsPerSector && smpIdx < samples.length; g++) {
         const byteOff = secBase + g * 3;
         const s0 = samples[smpIdx++];
         const s1 = smpIdx < samples.length ? samples[smpIdx++] : 0;
         packGroup(out, byteOff, s0, s1);
      }
   }
   return out;
}

/**
 * Encode 16-bit PCM samples into the DSS-1 disk sector layout.
 *
 * The first disk sector contains a WTB header (349 bytes) followed by 675 bytes
 * of packed sample data (225 groups = 450 samples, no padding since 675 % 3 = 0).
 * Subsequent sectors each contain 1023 bytes of data (341 groups = 682 samples)
 * with 1 padding byte at the end.
 *
 * @param {Int16Array} samples - 16-bit PCM samples
 * @returns {Uint8Array} - packed bytes in disk sector layout (without WTB header)
 */
export function encodeDss1DiskSamples(samples) {
   const FIRST_DATA_BYTES = DSS1_SECTOR_SIZE - DSS1_WTB_HEADER_SIZE; // 675
   const FIRST_GROUPS = Math.floor(FIRST_DATA_BYTES / 3);            // 225
   const FIRST_SAMPLES = FIRST_GROUPS * 2;                            // 450
   const GROUPS_PER_SECTOR = Math.floor(DSS1_SECTOR_SIZE / 3);       // 341
   const SAMPLES_PER_SECTOR = GROUPS_PER_SECTOR * 2;                  // 682

   // Calculate total sectors needed for data
   let remaining = samples.length;
   const firstSecSamples = Math.min(remaining, FIRST_SAMPLES);
   remaining -= firstSecSamples;
   const numDataSectors = Math.ceil(remaining / SAMPLES_PER_SECTOR);
   const totalSectors = 1 + numDataSectors;
   const totalBytes = totalSectors * DSS1_SECTOR_SIZE;
   const out = new Uint8Array(totalBytes);

   let smpIdx = 0;

   // First sector: 225 groups (675 bytes) at start of output, no padding
   for (let g = 0; g < FIRST_GROUPS && smpIdx < samples.length; g++) {
      const s0 = samples[smpIdx++];
      const s1 = smpIdx < samples.length ? samples[smpIdx++] : 0;
      packGroup(out, g * 3, s0, s1);
   }

   // Subsequent sectors: 341 groups (1023 bytes) + 1 padding byte each
   for (let sec = 1; sec < totalSectors && smpIdx < samples.length; sec++) {
      const secBase = sec * DSS1_SECTOR_SIZE;
      for (let g = 0; g < GROUPS_PER_SECTOR && smpIdx < samples.length; g++) {
         const s0 = samples[smpIdx++];
         const s1 = smpIdx < samples.length ? samples[smpIdx++] : 0;
         packGroup(out, secBase + g * 3, s0, s1);
      }
      // Last byte of sector (byte 1023) is padding (already 0)
   }

   return out;
}

/**
 * Build a WTB multisound header (349 bytes = 0x15D).
 *
 * Header layout:
 *   0x00-0x07: Multisound name (8 chars, ASCII, space-padded)
 *   0x08-0x0A: Total sample length (3 bytes LE) — number of samples
 *   0x0B:      Low nibble = numSounds (0 = 16), High nibble = ???
 *   0x0C:      maxInterval (used for sample rate detection)
 *   0x0D+:     Sound entries (21 bytes each for DSS-1)
 *
 * Sound entry (21 bytes):
 *   byte 0:    topKey (upper MIDI key)
 *   byte 1:    origKey (MIDI note at original pitch)
 *   byte 2:    relative-tune
 *   byte 3:    relative-level
 *   byte 4:    relative-cutoff
 *   bytes 5-7: word-length (sample count, 3 bytes LE)
 *   bytes 8-10: start-address (offset in multisound data, 3 bytes LE)
 *   bytes 11-13: loop-end (relative to start-address, 3 bytes LE)
 *   bytes 14-16: loop-start (absolute in multisound, 3 bytes LE)
 *   byte 17:   flags
 *   bytes 18-20: extra (DSS-1 only)
 */
export function buildWtbHeader(name, numSamples, sampleRate, origKey, loopStart, loopEnd) {
   const header = new Uint8Array(DSS1_WTB_HEADER_SIZE);

   // Name (8 chars, space-padded, uppercase)
   const nameBytes = new TextEncoder().encode(name.toUpperCase().substring(0, 8));
   for (let i = 0; i < 8; i++) {
      header[i] = i < nameBytes.length ? nameBytes[i] : 0x20; // space pad
   }

   // Total sample length (3 bytes LE)
   header[8]  = numSamples & 0xFF;
   header[9]  = (numSamples >> 8) & 0xFF;
   header[10] = (numSamples >> 16) & 0xFF;

   // numSounds = 1 (single segment), stored in low nibble of byte 0x0B
   // 0 means 16, so we store 1 directly
   header[0x0B] = 0x01;

   // maxInterval: determine from sample rate
   // f values: -12 (16kHz), -7 (24kHz), 0 (32kHz), +5 (48kHz)
   // maxInterval = maxDiff + f, where maxDiff = topKey - origKey = 0 (single sound, topKey=origKey)
   let f;
   switch (sampleRate) {
      case 16000: f = -12; break;
      case 24000: f = -7;  break;
      case 48000: f = 5;   break;
      case 32000:
      default:    f = 0;   break;
   }
   const maxInterval = (f & 0x7F);
   header[0x0C] = maxInterval;

   // Sound entry at offset 0x0D (21 bytes)
   const seOff = 0x0D;
   header[seOff + 0]  = origKey & 0x7F;       // topKey
   header[seOff + 1]  = origKey & 0x7F;       // origKey
   header[seOff + 2]  = 0x40;                 // relative-tune (centered)
   header[seOff + 3]  = 0x7F;                 // relative-level (max)
   header[seOff + 4]  = 0x00;                 // relative-cutoff (off)
   // word-length (3 bytes LE)
   header[seOff + 5]  = numSamples & 0xFF;
   header[seOff + 6]  = (numSamples >> 8) & 0xFF;
   header[seOff + 7]  = (numSamples >> 16) & 0xFF;
   // start-address = 0 (single sound starts at beginning)
   header[seOff + 8]  = 0;
   header[seOff + 9]  = 0;
   header[seOff + 10] = 0;
   // loop-end (relative to start-address)
   const leRel = loopEnd;
   header[seOff + 11] = leRel & 0xFF;
   header[seOff + 12] = (leRel >> 8) & 0xFF;
   header[seOff + 13] = (leRel >> 16) & 0xFF;
   // loop-start (absolute in multisound data)
   header[seOff + 14] = loopStart & 0xFF;
   header[seOff + 15] = (loopStart >> 8) & 0xFF;
   header[seOff + 16] = (loopStart >> 16) & 0xFF;
   // flags: 0x01 = has loop
   header[seOff + 17] = (loopEnd > loopStart) ? 0x01 : 0x00;
   // bytes 18-20: extra DSS-1 bytes (0)
   header[seOff + 18] = 0;
   header[seOff + 19] = 0;
   header[seOff + 20] = 0;

   return header;
}

/**
 * Build a directory entry (64 bytes).
 *
 * Layout:
 *   byte 0:    0x00 (entry marker)
 *   byte 1:    idx (continuation index, 0 for first entry)
 *   byte 2:    id (unique file ID)
 *   bytes 3-10: name (8 chars, ASCII, space-padded)
 *   bytes 11-13: type string ("WTB")
 *   byte 14:   numSectors (sectors in this entry fragment, max 0x18=24)
 *   byte 15:   0x00
 *   bytes 16+: sector map (2 bytes per sector: byte0=sector|head flag, byte1=track)
 *
 * Sector map encoding (from parser's parseDss1DirEntry + s2o):
 *   Physical sector number p maps to disk offset p * 1024 (no interleave).
 *   The parser decodes: s = (byte0 & 0x7F) - 1; head = (byte0 & 0x80) ? 1 : 0;
 *                       track = byte1; physical = track * 10 + s + head * 5
 *   So to encode physical sector p:
 *     head = (p % 10) >= 5 ? 1 : 0
 *     s    = p % 5
 *     track = Math.floor(p / 10)
 *     byte0 = ((s + 1) & 0x7F) | (head ? 0x80 : 0x00)
 *     byte1 = track
 */
function buildDirEntry(idx, id, name, physicalSectors) {
   const entry = new Uint8Array(64);
   entry[0] = 0x00; // marker
   entry[1] = idx & 0xFF;
   entry[2] = id & 0xFF;

   // Name (8 chars, space-padded, uppercase)
   const nameBytes = new TextEncoder().encode(name.toUpperCase().substring(0, 8));
   for (let i = 0; i < 8; i++) {
      entry[3 + i] = i < nameBytes.length ? nameBytes[i] : 0x20;
   }

   // Type "WTB"
   entry[11] = 0x57; // W
   entry[12] = 0x54; // T
   entry[13] = 0x42; // B

   // Number of sectors in this fragment (max 0x18 = 24 per entry)
   const ns = Math.min(physicalSectors.length, 0x18);
   entry[14] = ns;
   entry[15] = 0x00;

   // Sector map
   for (let j = 0; j < ns; j++) {
      const p = physicalSectors[j];
      const head = (p % 10) >= 5 ? 1 : 0;
      const s = p % 5;
      const track = Math.floor(p / 10);

      entry[16 + 2 * j]     = ((s + 1) & 0x7F) | (head ? 0x80 : 0x00);
      entry[16 + 2 * j + 1] = track & 0xFF;
   }

   return entry;
}

/**
 * Build a complete DSS-1 disk image from a list of samples.
 *
 * @param {Array} samples - Array of { name, pcm: Int16Array, sampleRate, origKey, loopStart, loopEnd }
 * @param {Object} opts - { name: diskName }
 * @returns {Uint8Array} - Complete disk image (819200 bytes)
 */
export function buildDss1Disk(samples, opts = {}) {
   const diskName = (opts.name || 'DSS1 DISK').toUpperCase().substring(0, 8);
   const isDsm1 = !!opts.dsm1;
   const diskSize = isDsm1 ? DSM1_DISK_SIZE : DSS1_DISK_SIZE;
   const disk = new Uint8Array(diskSize);

   // Fill entire disk with 0xFF (unformatted)
   disk.fill(0xFF);

   // Clear BAM/Directory area to 0x00
   for (let i = DSS1_BAM; i < DSS1_BAM + DSS1_DIR; i++) {
      disk[i] = 0x00;
   }

   // Clear data area to 0x00
   for (let i = DSS1_DATA_OFFS; i < diskSize; i++) {
      disk[i] = 0x00;
   }

   // Data area starts at physical sector 9 (0x2400 / 1024 = 9)
   // Sectors 0-8 are used by BAM/directory
   const FIRST_DATA_SECTOR = Math.floor(DSS1_DATA_OFFS / DSS1_SECTOR_SIZE); // 9
   const LAST_SECTOR = Math.floor(diskSize / DSS1_SECTOR_SIZE);
   let nextSector = FIRST_DATA_SECTOR;
   let dirEntryIdx = 0;
   let fileId = 1;

   for (const smp of samples) {
      if (dirEntryIdx >= DSS1_MAX_DIR_ENTRIES) break;
      if (nextSector >= LAST_SECTOR) break;

      const pcm = smp.pcm;
      if (!pcm || pcm.length === 0) continue;

      // Encode samples to 12-bit packed disk sector layout
      // First sector: 675 bytes data (no padding), subsequent: 1023 bytes + 1 padding
      const packed = encodeDss1DiskSamples(pcm);
      const totalSectors = packed.length / DSS1_SECTOR_SIZE;

      // Check if we have enough free sectors
      if (nextSector + totalSectors > LAST_SECTOR) {
         console.warn(`[DSS-1 export] Not enough space for sample "${smp.name}", skipping`);
         continue;
      }

      // Allocate physical sector numbers
      const allocatedSectors = [];
      for (let i = 0; i < totalSectors; i++) {
         allocatedSectors.push(nextSector + i);
      }
      nextSector += totalSectors;

      // Build WTB header
      const origKey = smp.origKey !== undefined ? smp.origKey : 60;
      const loopStart = smp.loopStart || 0;
      const loopEnd = smp.loopEnd || pcm.length;
      const wtbHeader = buildWtbHeader(smp.name, pcm.length, smp.sampleRate, origKey, loopStart, loopEnd);

      // Write first sector: WTB header (349 bytes) + first 675 bytes of packed data
      // The packed output from encodeDss1DiskSamples is laid out as:
      //   [0..674]:  first sector data (675 bytes, 225 groups)
      //   [675..1023]: unused (rest of first 1024-byte block in packed output)
      //   [1024..2047]: second sector (1023 bytes data + 1 padding)
      //   [2048..3071]: third sector, etc.
      const firstSecOff = allocatedSectors[0] * DSS1_SECTOR_SIZE;
      disk.set(wtbHeader, firstSecOff);
      disk.set(packed.subarray(0, DSS1_SECTOR_SIZE - DSS1_WTB_HEADER_SIZE), firstSecOff + DSS1_WTB_HEADER_SIZE);

      // Write remaining sectors: each is a 1024-byte block in the packed output
      // starting at sector_index * 1024
      for (let i = 1; i < allocatedSectors.length; i++) {
         const secOff = allocatedSectors[i] * DSS1_SECTOR_SIZE;
         const srcStart = i * DSS1_SECTOR_SIZE;
         disk.set(packed.subarray(srcStart, srcStart + DSS1_SECTOR_SIZE), secOff);
      }

      // Write directory entry (may need multiple entries if > 24 sectors)
      const name = smp.name || `SMP${fileId}`;
      let sectorsRemaining = allocatedSectors;
      let fragIdx = 0;

      while (sectorsRemaining.length > 0 && dirEntryIdx < DSS1_MAX_DIR_ENTRIES) {
         const fragSectors = sectorsRemaining.slice(0, 0x18); // max 24 per entry
         sectorsRemaining = sectorsRemaining.slice(0x18);

         const dirEntry = buildDirEntry(fragIdx, fileId, name, fragSectors);
         const dirOff = DSS1_BAM + dirEntryIdx * 64;
         disk.set(dirEntry, dirOff);
         dirEntryIdx++;
         fragIdx++;
      }

      fileId++;
   }

   // Write BAM (Block Allocation Map) — mark used sectors
   // The BAM is at offset 0x400, before the directory entries.
   // Actually, the DSS-1 directory starts at 0x400 and entries are 64 bytes.
   // The BAM may be interleaved with directory entries or separate.
   // Based on the parser, directory entries start at 0x400 (DSS1_BAM).
   // The first 0x400 bytes (0x0000-0x03FF) appear to be boot/unused.
   // We'll mark the disk type byte at 0x380 to indicate standard (non-turbo).
   disk[0x380] = 0x00; // standard DSS-1 (not turbo)

   return disk;
}

/**
 * Calculate the disk space needed for a set of samples.
 * Returns the number of bytes the samples would occupy on the DSS-1 disk.
 *
 * @param {Array} samples - Array of { pcm: Int16Array }
 * @returns {number} - bytes used on disk
 */
export function calcDss1Capacity(samples) {
   let totalSectors = 0;
   const FIRST_DATA_BYTES = DSS1_SECTOR_SIZE - DSS1_WTB_HEADER_SIZE; // 675
   const FIRST_SAMPLES = Math.floor(FIRST_DATA_BYTES / 3) * 2;       // 450
   const GROUPS_PER_SECTOR = Math.floor(DSS1_SECTOR_SIZE / 3);       // 341
   const SAMPLES_PER_SECTOR = GROUPS_PER_SECTOR * 2;                  // 682

   for (const smp of samples) {
      const pcm = smp.pcm;
      if (!pcm || pcm.length === 0) continue;

      // First sector holds 450 samples, subsequent sectors hold 682 each
      let remaining = pcm.length;
      const firstSecSamples = Math.min(remaining, FIRST_SAMPLES);
      remaining -= firstSecSamples;
      const numDataSectors = Math.ceil(remaining / SAMPLES_PER_SECTOR);
      totalSectors += 1 + numDataSectors;
   }

   return totalSectors * DSS1_SECTOR_SIZE;
}

/**
 * Check if a set of samples fits on a DSS-1 disk.
 */
export function fitsDss1Disk(samples) {
   return calcDss1Capacity(samples) <= (DSS1_DATA_SECTORS * DSS1_SECTOR_SIZE);
}
